import { createSlice } from "@reduxjs/toolkit";
import { userNormalLogout, supportLogout } from "../actions";

/**
 * Loading states for individual API calls
 */
export interface DashboardLoadingStates {
  isOverviewFetching: boolean;
  isSalesByRevenueFetching: boolean;
  isSalesByPaymentModeFetching: boolean;
  isBestsellerItemsFetching: boolean;
  isBestsellerCategoriesFetching: boolean;
  isBestsellerSubCategoriesFetching: boolean;
  isBestsellerBranchesFetching: boolean;
  isBestsellerBrandsFetching: boolean;
  isOnlineSalesFetching: boolean;
  isSalesDiscountsFetching: boolean;
  isParametersFetching: boolean;
  isSaleStatusFetching: boolean;
  isSellingPriceFetching: boolean;
  isSellTransactionFetching: boolean;
  isHourlyRevenueFetching: boolean;
}

export interface DashboardState extends DashboardLoadingStates {
  bestsellerItems: {};
  bestsellerCategories: {};
  bestsellerSubCategories: {};
  saleStatusData: {},
  bestsellerBranches: {};
  bestsellerBrands: {};
  salesOverview: {};
  parameters: {};
  channelReport: {};
  salesByPaymentMode: {};
  salesByRevenue: [];
  salesDiscounts: [];
  onlineSales: [];
  dashboardFilterState: {
    [key:string]:any
  };
  sellingPriceDetail: {},
  sellTransactionData: {}
  currentSale: [],
  oosItems: [],
  lastUpdate: '',
  hourlyRevenue: [],
  hourlyRevenueMetadata: any
}

const initialState: DashboardState = {
  // Loading states
  isOverviewFetching: false,
  isSalesByRevenueFetching: false,
  isSalesByPaymentModeFetching: false,
  isBestsellerItemsFetching: false,
  isBestsellerCategoriesFetching: false,
  isBestsellerSubCategoriesFetching: false,
  isBestsellerBranchesFetching: false,
  isBestsellerBrandsFetching: false,
  isOnlineSalesFetching: false,
  isSalesDiscountsFetching: false,
  isParametersFetching: false,
  isSaleStatusFetching: false,
  isSellingPriceFetching: false,
  isSellTransactionFetching: false,
  isHourlyRevenueFetching: false,
  // Data states
  bestsellerItems: {},
  bestsellerCategories: {},
  bestsellerSubCategories: {},
  saleStatusData: {},
  bestsellerBranches: {},
  bestsellerBrands: {},
  salesOverview: {},
  parameters: {},
  channelReport: {},
  salesByPaymentMode: {},
  salesByRevenue: [],
  salesDiscounts: [],
  onlineSales: [],
  dashboardFilterState: {},
  sellingPriceDetail: {},
  sellTransactionData: {},
  currentSale: [],
  oosItems: [],
  lastUpdate: '',
  hourlyRevenue: [],
  hourlyRevenueMetadata: null
}

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    getBestsellersData: (state, actions) => {
      // Track loading state based on entity type
      const entity = actions.payload?.entity;
      if (entity === 'item') {
        state.isBestsellerItemsFetching = true;
      } else if (entity === 'category') {
        state.isBestsellerCategoriesFetching = true;
      } else if (entity === 'subcategory') {
        state.isBestsellerSubCategoriesFetching = true;
      }
    },
    getRankingsData: (state, actions) => {
      // Track loading state based on entity type
      const entity = actions.payload?.entity;
      if (entity === 'branch') {
        state.isBestsellerBranchesFetching = true;
      } else if (entity === 'brand') {
        state.isBestsellerBrandsFetching = true;
      }
    },
    getSalesOverviewData: (state, actions) => {
      state.isOverviewFetching = true;
    },
    getParametersData: (state, actions) => {
      state.isParametersFetching = true;
    },
    getChannelReportData: (state, actions) => {

    },
    getSalesByPaymentModeData: (state, actions) => {
      state.isSalesByPaymentModeFetching = true;
    },
    getSalesByRevenueData: (state, actions) => {
      state.isSalesByRevenueFetching = true;
    },
    getOnlineSalesData: (state, actions) => {
      state.isOnlineSalesFetching = true;
    },
    getSalesDiscountsData: (state, actions) => {
      state.isSalesDiscountsFetching = true;
    },
    getSalesStatusData: (state, actions) => {
      state.isSaleStatusFetching = true;
    },
    getAvgSellingPrice: (state, actions) => {
      state.isSellingPriceFetching = true;
    },
    getSalesTransactions: (state, actions) => {
      state.isSellTransactionFetching = true;
    },
    getCurrentSale: (state, action) => {

    },
    getOOSItems: (state,action) => {

    },
    getHourlyRevenueData: (state, actions) => {
      // Clear old data when new request is made to prevent showing stale data
      state.hourlyRevenue = [];
      state.isHourlyRevenueFetching = true;
    },
    saveOOSItems: (state, action) => {
      state.oosItems = action.payload || []
    },
    saveCurrentSale: (state, action) => {
      state.currentSale = action.payload
    },
    saveSalesTransactions: (state, actions) => {
      state.sellTransactionData = actions.payload;
      state.isSellTransactionFetching = false;
    },
    saveAvgSellingPrice: (state, actions) => {
      state.sellingPriceDetail = actions.payload;
      state.isSellingPriceFetching = false;
    },
    saveBestsellerItemsData: (state, actions) => {
      state.bestsellerItems = actions.payload;
      state.isBestsellerItemsFetching = false;
    },
    saveBestsellerCategoriesData: (state, actions) => {
      state.bestsellerCategories = actions.payload;
      state.isBestsellerCategoriesFetching = false;
    },
    saveBestsellerSubCategoriesData: (state, actions) => {
      state.bestsellerSubCategories = actions.payload;
      state.isBestsellerSubCategoriesFetching = false;
    },
    saveSalesStatusData: (state, actions) => {
      state.saleStatusData = actions.payload;
      state.isSaleStatusFetching = false;
    },
    saveBestsellerBranchesData: (state, actions) => {
      state.bestsellerBranches = actions.payload;
      state.isBestsellerBranchesFetching = false;
    },
    saveBestsellerBrandsData: (state, actions) => {
      state.bestsellerBrands = actions.payload;
      state.isBestsellerBrandsFetching = false;
    },
    saveSalesOverviewData: (state, actions) => {
      state.salesOverview = actions.payload;
      state.isOverviewFetching = false;
    },
    saveParametersData: (state, actions) => {
      state.parameters = actions.payload?.parameters || [];
      state.isParametersFetching = false;
    },
    saveSalesByPaymentModeData: (state, actions) => {
      state.salesByPaymentMode = actions.payload;
      state.isSalesByPaymentModeFetching = false;
    },
    saveSalesByRevenueData: (state, actions) => {
      state.salesByRevenue = actions.payload?.list || [];
      state.isSalesByRevenueFetching = false;
    },
    saveOnlineSalesData: (state, actions) => {
      state.onlineSales = actions.payload?.list || [];
      state.isOnlineSalesFetching = false;
    },
    saveSalesDiscountsData: (state, actions) => {
      state.salesDiscounts = actions.payload?.list || [];
      state.isSalesDiscountsFetching = false;
    },
    saveDashboardFilterState: (state, actions) => {
      state.dashboardFilterState = {
        ...state.dashboardFilterState,
        [actions.payload.key]:actions.payload.value
      };
    },
    saveRealTimeLastUpdate: (state, action) => {
       state.lastUpdate = action.payload
    },
    saveHourlyRevenueData: (state, actions) => {
      // Preserve full response structure to access metadata (like comparableDateRanges) even when hourlyRevenue is empty
      state.hourlyRevenue = actions.payload?.hourlyRevenue || [];
      state.isHourlyRevenueFetching = false;
      // Prefer top-level comparableDateRanges as the single source of truth for headers.
      if (actions.payload && actions.payload.comparableDateRanges) {
        state.hourlyRevenueMetadata = { comparableDateRanges: actions.payload.comparableDateRanges };
      } 
      else if (actions.payload?.hourlyRevenue && Array.isArray(actions.payload.hourlyRevenue) && actions.payload.hourlyRevenue.length > 0 && actions.payload.hourlyRevenue[0]?.periodValues?.comparableDateRanges) {
        state.hourlyRevenueMetadata = { comparableDateRanges: actions.payload.hourlyRevenue[0].periodValues.comparableDateRanges };
      } else {
        state.hourlyRevenueMetadata = null;
      }
    },
    // Reset all loading states to false (useful after errors or cleanup)
    resetLoadingStates: (state) => {
      state.isOverviewFetching = false;
      state.isSalesByRevenueFetching = false;
      state.isSalesByPaymentModeFetching = false;
      state.isBestsellerItemsFetching = false;
      state.isBestsellerCategoriesFetching = false;
      state.isBestsellerSubCategoriesFetching = false;
      state.isBestsellerBranchesFetching = false;
      state.isBestsellerBrandsFetching = false;
      state.isOnlineSalesFetching = false;
      state.isSalesDiscountsFetching = false;
      state.isParametersFetching = false;
      state.isSaleStatusFetching = false;
      state.isSellingPriceFetching = false;
      state.isSellTransactionFetching = false;
      state.isHourlyRevenueFetching = false;
    },
    // Set all loading states to true (call before dispatching all API calls)
    startAllDataFetching: (state) => {
      state.isOverviewFetching = true;
      state.isSalesByRevenueFetching = true;
      state.isSalesByPaymentModeFetching = true;
      state.isBestsellerItemsFetching = true;
      state.isBestsellerCategoriesFetching = true;
      state.isBestsellerSubCategoriesFetching = true;
      state.isBestsellerBranchesFetching = true;
      state.isBestsellerBrandsFetching = true;
      state.isOnlineSalesFetching = true;
      state.isSalesDiscountsFetching = true;
      state.isParametersFetching = true;
      state.isSaleStatusFetching = true;
      state.isSellingPriceFetching = true;
      state.isSellTransactionFetching = true;
      state.isHourlyRevenueFetching = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(userNormalLogout, () => initialState)
      .addCase(supportLogout, () => initialState);
  },
})

export const {
  getBestsellersData,
  getRankingsData,
  getSalesOverviewData,
  getParametersData,
  getChannelReportData,
  getSalesByPaymentModeData,
  getSalesByRevenueData,
  getOnlineSalesData,
  getSalesDiscountsData,
  getSalesStatusData, 
  getAvgSellingPrice,
  getSalesTransactions,

  saveBestsellerItemsData,
  saveBestsellerCategoriesData,
  saveBestsellerSubCategoriesData,
  saveSalesStatusData,
  saveBestsellerBranchesData,
  saveBestsellerBrandsData,
  saveSalesOverviewData,
  saveParametersData,
  // saveChannelReport,
  saveSalesByPaymentModeData,
  saveSalesByRevenueData,
  saveOnlineSalesData,
  saveSalesDiscountsData,

  saveDashboardFilterState,
  saveAvgSellingPrice,
  saveSalesTransactions,
  getCurrentSale,
  saveCurrentSale,
  getOOSItems,
  saveOOSItems,
  saveRealTimeLastUpdate,
  getHourlyRevenueData,
  saveHourlyRevenueData,
  resetLoadingStates,
  startAllDataFetching
} = dashboardSlice.actions;
export default dashboardSlice.reducer;